import { MouseEvent, useCallback } from 'react';

import {
  useClient as useClientApi,
  useClientManagers,
} from 'api/requests/client';
import { useCountries } from 'api/requests/country';
import { OnboardingStatusType } from 'api/types/entity';
import { parseFullName } from 'helpers';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate, RoutesId, usePageTitle } from 'libs/navigation';
import {
  useManagersForClients,
  useCheckIfPersonalAccountIsEditing,
} from 'modules/client/common/hooks';

import { NavigationBreadcrumbsTitles } from '../../../../components';
import { ClientFormType } from '../forms/clientForm';
import { getClientInitialValue } from '../helpers';

import { useClientActions } from './useClientActions';
import { useFormError } from './useFormError';
import { useLockClient } from './useLockClient';
import { useUpdateClient } from './useUpdateClient';

type ClientContainerParams = {
  clientId: string;
};

export const useClient = () => {
  const { t } = useTranslation();

  const { params, navigate } = useNavigate<ClientContainerParams>();

  const { loading, client, error, refetch } = useClientApi(params.clientId!);

  const { countries, loading: countriesLoading } = useCountries();

  const { updateClient, submitLoading, error: submitError } = useUpdateClient();

  const formError = useFormError(submitError);

  const { lockClientIfPossible, unlockClient } = useLockClient(client);
  const { managers, loading: managersLoading } =
    useManagersForClients(useClientManagers);

  const {
    onLinkToBright,
    onRevokeOtpVerification,
    onSendInvitationToAccount,
    onShowVersionHistory,
    revokeOtpVerificationLoading,
    onDownloadClientQuestionnaire,
    onDelete,
    deleteLoading,
  } = useClientActions({ client, lockClientIfPossible, refetch });

  const { isEditablePersonalAccountNumber } =
    useCheckIfPersonalAccountIsEditing({
      isAccountOpened:
        client?.onboardingStatus === OnboardingStatusType.AccountOpened,
      isCreatedBySystem: client?.personalAccount.isCreatedBySystem ?? false,
    });

  const hasRepresentative = !!client?.representativeId;

  const initialValues = client ? getClientInitialValue(client, t) : undefined;

  const fullName = client ? parseFullName(client) : '';

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.personClient]: fullName,
  };

  const onSubmit: FormSubmit<ClientFormType> = async (values, helpers) => {
    if (await updateClient(client!, values, isEditablePersonalAccountNumber)) {
      helpers.setReadOnly(true);
    }
  };

  const onClickRepresentative = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!client?.representativeId) {
        return;
      }

      navigate(
        ROUTES.clients.personClients.personClient.representativeClient.fullPath,
        {
          representativeId: client.representativeId,
          clientId: client.id,
        },
        { event }
      );
    },
    [navigate, client]
  );

  usePageTitle(fullName);

  return {
    client,
    loading: !client && loading,
    countries: countries ?? [],
    countriesLoading,
    error,
    submitLoading,
    hasRepresentative,
    initialValues,
    formError,
    managers,
    managersLoading,
    breadcrumbsTitles,
    revokeOtpVerificationLoading,
    deleteLoading,
    isEditablePersonalAccountNumber,
    lockClientIfPossible,
    unlockClient,
    onSubmit,
    onShowVersionHistory,
    onRevokeOtpVerification,
    onSendInvitationToAccount,
    onLinkToBright,
    clientId: params.clientId!,
    onClickRepresentative,
    onDownloadClientQuestionnaire,
    onDelete,
  };
};
